<script lang="ts" setup>
import userStore from '@/store/UserStore'
import projectStore from '@/store/ProjectStore'
import formStore from '@/store/FormStore'
import { StepsController } from '@/controllers/StepsController'
import { validateComponent, ValidatorType } from '@/helper/ValidateDirective'
import FormElement from '@/components/FormElement.vue'
import { BusEvent, HpStep, ProjectPropertyType } from '@/helper/Types'
import bus from '@/helper/Bus'
import InputSuffix from '@/components/InputSuffix.vue'
import { api } from '@/services/ApiService'
import { debounce } from '@/helper/tools'
import SectionHeadline from '@/components/SectionHeadline.vue'
import ComboBox from '@/components/ComboBox.vue'
import appStore from '@/store/AppStore'
import { computed, onMounted, onUnmounted, reactive, ref } from 'vue'
import i18n from '@/helper/Translations'

const validator: ValidatorType = reactive({})

const matchingProjectCities = ref<Record<string, string>[]>([])
const matchingCustomerCities = ref<Record<string, string>[]>([])
const translatedMatchingProjectCities = ref<string[]>([])
const translatedMatchingCustomerCities = ref<string[]>([])
const customerAddressDiffers = ref<boolean>(false)
const outdoorTemperature = ref<string | undefined>(undefined)
const outdoorTemperatureInitialized = ref<boolean>(false)

const outdoorTemperatureString = computed((): string => {
  return `${i18n.global.t('step_details.outdoor_temperature_info')} ${outdoorTemperature.value} ${i18n.global.t(
    'global.degree_celsius'
  )}`
})

const noOutdoorTemperatureFound = computed((): boolean => {
  const isTemperatureNotFound = !validator.projectPostalCode?.error && outdoorTemperature.value === undefined && formStore.data.projectPostalCode !== ''

  if (userStore.enableCityDropdown) {
    return isTemperatureNotFound && formStore.data.projectCity !== ''
  }

  return isTemperatureNotFound
})

setCustomerProjectDataDiff()

onMounted((): void => {
  onProjectPostalCodeInput()
  if (userStore.enableCityDropdown) {
    if (userStore.enableCustomerAddress) {
      onCustomerPostalCodeInput()
    }
  }

  bus.$onC(HpStep.PROJECT_DETAILS, BusEvent.LANGUAGE_CHANGED, () => {
    updateProjectCityOptions()
    updateCustomerCityOptions()

    if (userStore.enableCityDropdown) {
      const projectPostalCode = (formStore.data['projectPostalCode'] || '').toString().trim()
      const customerPostalCode = (formStore.data['customerPostalCode'] || '').toString().trim()

      if (projectPostalCode === customerPostalCode && projectPostalCode.length) {
        loadPostalCodesCities(projectPostalCode).then(postalCodeList => {
          matchingProjectCities.value = postalCodeList
          updateProjectCityOptions()
          matchingCustomerCities.value = postalCodeList
          updateCustomerCityOptions()
        })
      } else {
        if (projectPostalCode.length) {
          loadPostalCodesCities(projectPostalCode, 'projectPostalCode').then(postalCodeList => {
            matchingProjectCities.value = postalCodeList
            updateProjectCityOptions()
          })
        }

        if (customerPostalCode.length) {
          loadPostalCodesCities(customerPostalCode, 'customerPostalCode').then(postalCodeList => {
            matchingCustomerCities.value = postalCodeList
            updateCustomerCityOptions()
          })
        }
      }
    }
  })
})

onUnmounted((): void => {
  bus.$offC(HpStep.PROJECT_DETAILS)
})

function validate(): boolean {
  if (!customerAddressDiffers.value && userStore.enableCustomerAddress) {
    copyProjectIntoCustomerData()
  }

  if (noOutdoorTemperatureFound.value) {
    return false
  }

  return validateComponent(validator)
}

const loadOutdoorTemperature = debounce(() => {
  const postalCode = formStore.data.projectPostalCode
  let inputsAreValid = true

  if (userStore.enableAltitudeInput) {
    inputsAreValid = validator.projectAltitude.validate()
  }

  if (postalCode && inputsAreValid) {
    api.loadOutdoorTemperature(postalCode as string, formStore.data.projectCity as string, formStore.data.projectAltitude as string)
      .then(res => outdoorTemperature.value = res as string)
      .finally(() => (outdoorTemperatureInitialized.value = true))
  } else {
    outdoorTemperature.value = undefined
  }
}, 500)

function setCustomerProjectDataDiff(): void {
  const prop = projectStore.projectProperties
  customerAddressDiffers.value =
    ((prop.customerStreet.val || '').toString().trim() !== '' && prop.customerStreet.val !== prop.projectStreet.val) ||
    ((prop.customerHouseNumber.val || '').toString().trim() !== '' &&
      prop.customerHouseNumber.val !== prop.projectHouseNumber.val) ||
    ((prop.customerPostalCode.val || '').toString().trim() !== '' &&
      prop.customerPostalCode.val !== prop.projectPostalCode.val) ||
    ((prop.customerCity.val || '').toString().trim() !== '' && prop.customerCity.val !== prop.projectCity.val)
}

function copyProjectIntoCustomerData(): void {
  formStore.data.customerStreet = formStore.data.projectStreet
  formStore.data.customerHouseNumber = formStore.data.projectHouseNumber
  formStore.data.customerPostalCode = formStore.data.projectPostalCode
  formStore.data.customerCity = formStore.data.projectCity
}

function customerAddressToggleChanged(): void {
  formStore.data.customerStreet = ''
  formStore.data.customerHouseNumber = ''
  formStore.data.customerPostalCode = ''
  formStore.data.customerCity = ''
}

function onCustomerPostalCodeInput(): void {
  loadPostalCodeCities('customerPostalCode')?.then(postalCodeList => {
    matchingCustomerCities.value = postalCodeList
    userStore.validCustomerPostalCodes = postalCodeList.length
      ? [formStore.data.customerPostalCode as keyof ProjectPropertyType]
      : []
    updateCustomerCityOptions()
  })
}

function onProjectPostalCodeInput(): void {
  loadPostalCodeCities('projectPostalCode')?.then(postalCodeList => {
    matchingProjectCities.value = postalCodeList
    userStore.validProjectPostalCodes = postalCodeList.length
      ? [formStore.data.projectPostalCode as keyof ProjectPropertyType]
      : []
    updateProjectCityOptions()
  })
  loadOutdoorTemperature()
}

function loadPostalCodeCities(propCode: keyof ProjectPropertyType): Promise<Record<string, string>[]> | void {
  if (!userStore.enableCityDropdown) {
    return
  }
  const val = (formStore.data[propCode] || '').toString().trim()
  return loadPostalCodesCities(val, propCode)
}

function updateCustomerCityOptions(): void {
  if (!userStore.enableCityDropdown) {
    return
  }

  const propCode = 'customerPostalCode'
  const propCity = 'customerCity'
  const val = (formStore.data[propCode] || '').toString().trim()

  if (!val) {
    formStore.data[propCode] = ''
    matchingCustomerCities.value = []
    return
  }

  const matchingCities = matchingCustomerCities.value.map(item => item[userStore.currentLanguage] as string)
  translatedMatchingCustomerCities.value = matchingCities

  const formStoreData = formStore.data as Record<string, string>
  if (!matchingCustomerCities.value.length) {
    formStoreData[propCity] = ''
  } else if (matchingCustomerCities.value.length === 1) {
    formStoreData[propCity] = matchingCities[0]
  }
}

const projectCityOptions = computed(() => {
  return translatedMatchingProjectCities.value.map(city => ({
    label: city,
    value: city
  }))
})

const customerCityOptions = computed(() => {
  return translatedMatchingCustomerCities.value.map(city => ({
    label: city,
    value: city
  }))
})

function updateProjectCityOptions(): void {
  if (!userStore.enableCityDropdown) {
    return
  }

  const val = (formStore.data.projectPostalCode || '').toString().trim()

  if (!val) {
    formStore.data.projectPostalCode = ''
    matchingProjectCities.value = []
    return
  }

  const matchingCities = matchingProjectCities.value.map(item => item[userStore.currentLanguage] as string)
  translatedMatchingProjectCities.value = matchingCities

  if (!matchingProjectCities.value.length) {
    formStore.data.projectCity = ''
  } else if (matchingProjectCities.value.length === 1) {
    formStore.data.projectCity = matchingCities[0]
  }
}

async function loadPostalCodesCities(postalCode: string, propCode?: string): Promise<Record<string, string>[]> {
  if (!userStore.enableCityDropdown) {
    return []
  }
  if (
    (userStore.postalCodeValidationPattern || '').trim() !== '' &&
    !new RegExp(userStore.postalCodeValidationPattern || '').test(postalCode)
  ) {
    return []
  }
  appStore.isLoading = true
  try {
    let res: Record<string, string>[]

    if (propCode === 'customerPostalCode') {
      res = await api.loadCustomerCities(postalCode)
    } else {
      res = await api.loadProjectCities(postalCode)
    }

    return res.sort((p1, p2) => {
      const localizedCityA = p1[userStore.currentLanguage]
      const localizedCityB = p2[userStore.currentLanguage]

      if (localizedCityA == localizedCityB) {
        return 0
      } else {
        return localizedCityA < localizedCityB ? -1 : 1
      }
    })
  } catch (e) {
    window.console.error(`Error fetching cities to postal codes "${postalCode}":`, e)
    return []
  } finally {
    appStore.isLoading = false
  }
}

defineExpose({
  validator,
  validate
})
</script>

<template>
  <div class="steps__content">
    <form
      id="project-detail-form"
      action=""
      class="form card steps__form"
      @submit.prevent="StepsController.nextClicked()"
    >
      <div class="form__section">
        <div class="form__section-inner">
          <SectionHeadline class="form__title" translation-key="step_details.title_project" />
          <div class="row form__row">
            <div class="col-md-6">
              <FormElement
                v-validate="projectStore.projectProperties.name.formValidationRule"
                :error="validator.projectName?.error"
                :required="projectStore.propertyIsRequired('name')"
                label-key="step_details.project_name"
              >
                <input
                  id="project-name"
                  v-model="formStore.data.name"
                  :placeholder="$t('step_details.placeholder_project_name')"
                  class="firstfocus"
                  data-hj-suppress
                  type="text"
                >
              </FormElement>
            </div>
          </div>
          <div class="row form__row">
            <div class="col-md-6">
              <FormElement
                v-validate="projectStore.projectProperties.projectStreet.formValidationRule"
                :error="validator.projectStreetName?.error"
                :required="projectStore.propertyIsRequired('projectStreet')"
                label-key="step_details.street_name"
              >
                <input
                  id="project-street-name"
                  v-model="formStore.data.projectStreet"
                  :placeholder="$t('step_details.placeholder_street_name')"
                  data-hj-suppress
                  type="text"
                >
              </FormElement>
            </div>
            <div class="col-md-6">
              <FormElement
                v-validate="projectStore.projectProperties.projectHouseNumber.formValidationRule"
                :error="validator.projectHouseNumber?.error"
                :required="projectStore.propertyIsRequired('projectHouseNumber')"
                label-key="step_details.house_number"
              >
                <input
                  id="project-house-number"
                  v-model="formStore.data.projectHouseNumber"
                  :placeholder="$t('step_details.placeholder_house_number')"
                  data-hj-suppress
                  type="text"
                >
              </FormElement>
            </div>
          </div>
          <div class="row form__row">
            <div class="col-md-6 project-postal-code">
              <FormElement
                v-validate="projectStore.projectProperties.projectPostalCode.formValidationRule"
                :error="validator.projectPostalCode?.error"
                :required="projectStore.propertyIsRequired('projectPostalCode')"
                label-key="step_details.postal_code"
              >
                <input
                  id="project-postal-code"
                  v-model="formStore.data.projectPostalCode"
                  :placeholder="$t('step_details.placeholder_postal_code')"
                  data-hj-whitelist
                  name="project-postal-code"
                  type="text"
                  @input="onProjectPostalCodeInput"
                >
              </FormElement>
              <p v-if="outdoorTemperature !== undefined" class="form__info" data-hj-whitelist>
                {{ outdoorTemperatureString }}
              </p>
              <p v-if="noOutdoorTemperatureFound && outdoorTemperatureInitialized" class="form__info form__info--error">
                {{ $t('step_details.outdoor_temperature_error') }}
              </p>
            </div>
            <div class="col-md-6 project-city">
              <FormElement
                v-validate="projectStore.projectProperties.projectCity.formValidationRule"
                :error="validator.projectCity?.error"
                :required="projectStore.propertyIsRequired('projectCity')"
                label-key="step_details.city"
              >
                <ComboBox
                  v-if="userStore.enableCityDropdown"
                  id="project-city"
                  v-model="formStore.data.projectCity"
                  :disabled="!projectCityOptions.length"
                  :options="projectCityOptions"
                  @update:model-value="loadOutdoorTemperature"
                />

                <input
                  v-else
                  id="project-city"
                  v-model="formStore.data.projectCity"
                  :placeholder="$t('step_details.placeholder_city')"
                  data-hj-whitelist
                  type="text"
                >
              </FormElement>
            </div>
          </div>
          <div v-if="userStore.enableAltitudeInput" class="row form__row">
            <div class="col-md-6">
              <FormElement
                v-validate="projectStore.projectProperties.projectAltitude.formValidationRule"
                :error="validator.projectAltitude?.error"
                :required="projectStore.propertyIsRequired('projectAltitude')"
                label-key="step_details.altitude"
              >
                <InputSuffix
                  id="project-altitude"
                  v-model="formStore.data.projectAltitude"
                  :placeholder="$t('step_details.placeholder_project_altitude')"
                  class="inputsuffix__wrapper--full"
                  data-hj-whitelist
                  suffix=" m"
                  @update:model-value="loadOutdoorTemperature"
                />
              </FormElement>
            </div>
          </div>
        </div>
      </div>
      <div class="form__section">
        <div class="form__section-inner">
          <SectionHeadline class="form__title" translation-key="step_details.title_customer" />
          <div class="row form__row">
            <div class="col-md-6">
              <FormElement
                v-validate="projectStore.projectProperties.firstName.formValidationRule"
                :error="validator.firstName?.error"
                :required="projectStore.propertyIsRequired('firstName')"
                label-key="step_details.first_name"
              >
                <input
                  id="first-name"
                  v-model="formStore.data.firstName"
                  :placeholder="$t('step_details.placeholder_first_name')"
                  data-hj-suppress
                  type="text"
                >
              </FormElement>
            </div>
            <div class="col-md-6">
              <FormElement
                v-validate="projectStore.projectProperties.lastName.formValidationRule"
                :error="validator.lastName?.error"
                :required="projectStore.propertyIsRequired('lastName')"
                label-key="step_details.last_name"
              >
                <input
                  id="last-name"
                  v-model="formStore.data.lastName"
                  :placeholder="$t('step_details.placeholder_last_name')"
                  data-hj-suppress
                  type="text"
                >
              </FormElement>
            </div>
          </div>

          <div class="row form__row">
            <div class="col-md-6">
              <FormElement
                v-validate="projectStore.projectProperties.phoneNumber.formValidationRule"
                :error="validator.phoneNumber?.error"
                :required="projectStore.propertyIsRequired('phoneNumber')"
                label-key="step_details.phone_number"
              >
                <input
                  id="phone-number"
                  v-model="formStore.data.phoneNumber"
                  :placeholder="$t('step_details.placeholder_phone_number')"
                  data-hj-suppress
                  type="text"
                >
              </FormElement>
            </div>
            <div class="col-md-6">
              <FormElement
                v-validate="projectStore.projectProperties.emailAddress.formValidationRule"
                :error="validator.emailAddress?.error"
                :required="projectStore.propertyIsRequired('emailAddress')"
                label-key="step_details.email"
              >
                <input
                  id="email-address"
                  v-model="formStore.data.emailAddress"
                  :placeholder="$t('step_details.placeholder_email')"
                  data-hj-suppress
                  type="text"
                >
              </FormElement>
            </div>
          </div>
          <div v-if="userStore.enableCustomerAddress" class="form-segment-toggle">
            <label class="form__checkbox form-segment-toggle__checkbox">
              {{ $t('step_details.customer_address_differs_text') }}
              <input
                id="cb-customer-address-differs"
                v-model="customerAddressDiffers"
                type="checkbox"
                @change="customerAddressToggleChanged"
              >
              <span class="form__cm is--large" />
            </label>
          </div>
          <div v-if="userStore.enableCustomerAddress && customerAddressDiffers" class="row form__row">
            <div class="col-md-6">
              <FormElement
                v-validate="projectStore.projectProperties.customerStreet.formValidationRule"
                :error="validator.customerStreetName?.error"
                :required="projectStore.propertyIsRequired('customerStreet')"
                label-key="step_details.street_name"
              >
                <input
                  id="customer-street-name"
                  v-model="formStore.data.customerStreet"
                  :placeholder="$t('step_details.placeholder_street_name')"
                  data-hj-suppress
                  type="text"
                >
              </FormElement>
            </div>
            <div class="col-md-6">
              <FormElement
                v-validate="projectStore.projectProperties.customerHouseNumber.formValidationRule"
                :error="validator.customerHouseNumber?.error"
                :required="projectStore.propertyIsRequired('customerHouseNumber')"
                label-key="step_details.house_number"
              >
                <input
                  id="customer-house-number"
                  v-model="formStore.data.customerHouseNumber"
                  :placeholder="$t('step_details.placeholder_house_number')"
                  data-hj-suppress
                  type="text"
                >
              </FormElement>
            </div>
          </div>
          <div v-if="userStore.enableCustomerAddress && customerAddressDiffers" class="row form__row">
            <div class="col-md-6">
              <FormElement
                v-validate="projectStore.projectProperties.customerPostalCode.formValidationRule"
                :error="validator.customerPostalCode?.error"
                :required="projectStore.propertyIsRequired('customerPostalCode')"
                label-key="step_details.postal_code"
              >
                <input
                  id="customer-postal-code"
                  v-model="formStore.data.customerPostalCode"
                  :placeholder="$t('step_details.placeholder_postal_code')"
                  data-hj-whitelist
                  name="customer-postal-code"
                  type="text"
                  @input="onCustomerPostalCodeInput"
                >
              </FormElement>
            </div>
            <div class="col-md-6">
              <FormElement
                v-validate="projectStore.projectProperties.customerCity.formValidationRule"
                :error="validator.customerCity?.error"
                :required="projectStore.propertyIsRequired('customerCity')"
                label-key="step_details.city"
              >
                <ComboBox
                  v-if="userStore.enableCityDropdown"
                  id="customer-city"
                  v-model="formStore.data.customerCity"
                  :disabled="!customerCityOptions.length"
                  :options="customerCityOptions"
                  data-hj-whitelist
                />

                <input
                  v-else
                  id="customer-city"
                  v-model="formStore.data.customerCity"
                  :placeholder="$t('step_details.placeholder_city')"
                  data-hj-whitelist
                  type="text"
                >
              </FormElement>
            </div>
          </div>
        </div>
      </div>
      <input class="is--invisible" type="submit">
    </form>
  </div>
</template>

<style lang="scss">
.form__sub-label {
  margin-bottom: rem-calc(8);
}

.form__info {
  color: $textColorLight;
  margin-bottom: 1rem;
  line-height: 1;
  text-overflow: ellipsis;
  margin-top: -5px;

  &--error {
    color: $errorColor;
  }
}

.form-segment-toggle {
  display: flex;
  align-items: center;
  margin-bottom: rem-calc(24);
  margin-top: rem-calc(8);

  &__checkbox {
    margin: 0;
    display: flex;
    width: 100%;
  }

  .form__cm {
    margin-left: auto;
    margin-right: 0;
  }
}

.steps {
  &__form {
    overflow: visible;
  }
}
</style>
